import { useRef, useCallback, useState } from 'react'

const FROZEN_COUNT = 2          // freeze the first two columns
const DEFAULT_FROZEN_W = 100    // default width for frozen cols if never resized

export function useResizableColumns(tableKey = 'default') {
  const storageKey = `col_widths_${tableKey}`
  function loadWidths() {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}') }
    catch { return {} }
  }
  const widths = useRef(loadWidths())
  // bump to re-render when a frozen column is resized (so offsets recompute)
  const [, force] = useState(0)

  const widthOf = useCallback((colKey, isFrozen) => {
    const w = widths.current[colKey]
    if (w) return w
    return isFrozen ? DEFAULT_FROZEN_W : undefined
  }, [])

  const onMouseDown = useCallback((e, colKey, isFrozen) => {
    e.preventDefault()
    e.stopPropagation()
    const th = e.currentTarget.parentElement
    if (!th) return
    const startX = e.clientX
    const startWidth = th.offsetWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    function onMouseMove(ev) {
      const newWidth = Math.max(60, startWidth + (ev.clientX - startX))
      th.style.width = newWidth + 'px'
      th.style.minWidth = newWidth + 'px'
      widths.current[colKey] = newWidth
    }
    function onMouseUp() {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      try { localStorage.setItem(storageKey, JSON.stringify(widths.current)) } catch {}
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      // frozen column resized -> the sticky offset of col 2 must update
      if (isFrozen) force(n => n + 1)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [storageKey])

  // th props — index tells us whether this column is frozen
  const getThProps = useCallback((colKey, index = -1, cols = []) => {
    const isFrozen = index >= 0 && index < FROZEN_COUNT
    const w = widthOf(colKey, isFrozen)
    const base = {
      width: w ? w + 'px' : undefined,
      minWidth: w ? w + 'px' : '80px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    }
    if (!isFrozen) return { style: { ...base, position: 'relative' } }
    // compute left offset from the real widths of preceding frozen columns
    let left = 0
    for (let i = 0; i < index; i++) {
      const k = cols[i]?.key
      left += widthOf(k, true) || DEFAULT_FROZEN_W
    }
    return {
      style: {
        ...base,
        position: 'sticky',
        left: left + 'px',
        zIndex: 8,
        background: 'var(--bg3)',
        borderRight: index === FROZEN_COUNT - 1 ? '2px solid var(--border2)' : '1px solid var(--border)',
      }
    }
  }, [widthOf])

  // matching td props so the body columns align with the header
  const getTdProps = useCallback((colKey, index = -1, cols = [], rowIndex = 0) => {
    const isFrozen = index >= 0 && index < FROZEN_COUNT
    if (!isFrozen) return {}
    const w = widthOf(colKey, true)
    let left = 0
    for (let i = 0; i < index; i++) {
      const k = cols[i]?.key
      left += widthOf(k, true) || DEFAULT_FROZEN_W
    }
    return {
      style: {
        position: 'sticky',
        left: left + 'px',
        zIndex: 3,
        width: w + 'px',
        minWidth: w + 'px',
        background: rowIndex % 2 === 0 ? 'var(--bg2)' : '#10192b',
        borderRight: index === FROZEN_COUNT - 1 ? '2px solid var(--border2)' : '1px solid var(--border)',
      }
    }
  }, [widthOf])

  const Resizer = useCallback(({ colKey, index = -1 }) => (
    <div
      onMouseDown={(e) => onMouseDown(e, colKey, index >= 0 && index < FROZEN_COUNT)}
      style={{
        position: 'absolute',
        right: 0, top: 0, bottom: 0,
        width: 6,
        cursor: 'col-resize',
        zIndex: 10,
        background: 'transparent',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(30,111,191,0.6)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    />
  ), [onMouseDown])

  return { getThProps, getTdProps, Resizer }
}
